import React, { useEffect, useState } from 'react';
import {
    Avatar, Card, Col, Form, Row, Space, Typography, message, Button,
    Tag, Input, Tooltip, Modal, InputNumber, Badge, Upload,
    Select, Grid
} from 'antd';
import {
    EnvironmentOutlined, GlobalOutlined, EditOutlined, ShopOutlined, MailOutlined,
    PhoneOutlined, CreditCardOutlined, InfoCircleOutlined, LinkOutlined, CopyOutlined,
    TagsOutlined, DatabaseOutlined
} from '@ant-design/icons';
import { COLOMBIA_DATA } from '../../../constants/colombiaData';
import ModalConfiguracionCartera from '../modals/ModalConfiguracionCartera';
import ModalPuntoDeVentaPos from '../modals/ModalPuntoDeVentaPos';

const { useBreakpoint } = Grid;
const { Title, Text } = Typography;

const SeccionEmpresa = ({ config, configLoading, updateConfig, updateEmpresa }) => {

    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const diasGuardado = config.settings?.dias_vencimiento_cartera !== undefined
        ? Number(config.settings.dias_vencimiento_cartera)
        : 30;

    const [isModalCarteraOpen, setIsModalCarteraOpen] = useState(false);
    const [guardandoCartera, setGuardandoCartera] = useState(false);
    const [isModalEmpresaOpen, setIsModalEmpresaOpen] = useState(false);
    const [logoFileBase64, setLogoFileBase64] = useState(null);
    const [departamentoActivo, setDepartamentoActivo] = useState(null);
    const [isModalDirEmpresaOpen, setIsModalDirEmpresaOpen] = useState(false);
    const [isModalRegionOpen, setIsModalRegionOpen] = useState(false);
    const [isModalCatalogoOpen, setIsModalCatalogoOpen] = useState(false);
    const [isModalTarifasOpen, setIsModalTarifasOpen] = useState(false);
    const [isModalPosOpen, setIsModalPosOpen] = useState(false);
    const [isModalInventarioOpen, setIsModalInventarioOpen] = useState(false);
    const [isModalPedidosOpen, setIsModalPedidosOpen] = useState(false);
    const [formPedidos] = Form.useForm();
    const [formInventario] = Form.useForm();
    const [formPos] = Form.useForm();
    const [formTarifas] = Form.useForm();
    const [formCartera] = Form.useForm();
    const [formEmpresa] = Form.useForm();
    const [formRegion] = Form.useForm();
    const [formDirEmpresa] = Form.useForm();
    const [formCatalogo] = Form.useForm();

    const [dominioBase, setDominioBase] = useState(".tienda.yugonub.com");
    const [protocolo, setProtocolo] = useState("https://");

    const defaultTarifas = config.config_tarifas || {
        lista_1: { activa: false, nombre: "Precio Lista 1" },
        lista_2: { activa: false, nombre: "Precio Lista 2" },
        lista_3: { activa: false, nombre: "Precio Lista 3" }
    };

    const handleGuardarTarifas = async (values) => {
        try {
            const payload = {
                config_tarifas: {
                    lista_1: { activa: values.lista_1_activa, nombre: values.lista_1_nombre },
                    lista_2: { activa: values.lista_2_activa, nombre: values.lista_2_nombre },
                    lista_3: { activa: values.lista_3_activa, nombre: values.lista_3_nombre }
                }
            };
            await updateEmpresa(payload);
            message.success('Listas de precios actualizadas correctamente');
            setIsModalTarifasOpen(false);
        } catch (error) {
            message.error(error.response?.data?.error || 'Error al guardar las tarifas');
        }
    };

    useEffect(() => {
        const hostname = window.location.hostname;
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            setDominioBase(".localhost:3000");
            setProtocolo("http://");
        } else if (hostname.includes('dev')) {
            setDominioBase(".tiendadev.yugonub.com");
            setProtocolo("https://");
        } else {
            setDominioBase(".tienda.yugonub.com");
            setProtocolo("https://");
        }
    }, []);

    const copiarAlPortapapeles = (texto) => {
        navigator.clipboard.writeText(texto);
        message.success('Enlace copiado al portapapeles');
    };

    const handleOpenModalCartera = () => {
        formCartera.setFieldsValue({ dias_vencimiento_cartera: diasGuardado });
        setIsModalCarteraOpen(true);
    };

    const handleSubmitCartera = async (values) => {
        setGuardandoCartera(true);
        try {
            const dias = Number(values.dias_vencimiento_cartera) ?? 30;
            await updateConfig({ dias_vencimiento_cartera: String(dias) });
            message.success('Configuración de cartera guardada correctamente');
            setIsModalCarteraOpen(false);
        } catch (err) {
            message.error(err.response?.data?.error || 'Error al guardar');
        } finally {
            setGuardandoCartera(false);
        }
    };

    const handleGuardarEmpresa = async (values) => {
        try {
            if (logoFileBase64) {
                values.logo_url = logoFileBase64;
            }
            await updateEmpresa(values);
            message.success('Información legal actualizada correctamente');
            setIsModalEmpresaOpen(false);
            setLogoFileBase64(null);
        } catch (error) {
            message.error(error.response?.data?.error || 'Hubo un error al actualizar los datos');
        }
    };

    const handleGuardarDirEmpresa = async (values) => {
        try {
            await updateEmpresa(values);
            message.success('Dirección de la empresa actualizada correctamente');
            setIsModalDirEmpresaOpen(false);
        } catch (error) {
            message.error(error.response?.data?.error || 'Error al actualizar la dirección');
        }
    };

    const handleGuardarRegion = async (values) => {
        try {
            await updateConfig(values);
            message.success('Configuración regional actualizada');
            setIsModalRegionOpen(false);
        } catch (error) {
            message.error(error.response?.data?.error || 'Error al actualizar configuración');
        }
    };

    const handleGuardarCatalogo = async (values) => {
        try {
            const subdominioLimpio = values.subdominio.toLowerCase().trim();
            // Validar que no vuelva a guardar el subdominio si no tiene cambios
            if (config.subdominio === subdominioLimpio) {
                setIsModalCatalogoOpen(false);
                return;
            }
            await updateEmpresa({ subdominio: subdominioLimpio });
            message.success('Enlace de tu catálogo configurado correctamente');
            setIsModalCatalogoOpen(false);
        } catch (error) {
            // Si el backend lanza error de Unique Constraint (código 23505 en postgres), lo capturamos aquí
            if (error.response?.data?.error?.includes("duplicado") || error.response?.status === 400) {
                message.error('Este enlace ya está en uso por otra tienda. Intenta con otro nombre.');
            } else {
                message.error(error.response?.data?.error || 'Error al crear el enlace.');
            }
        }
    };

    const urlEcomerce = protocolo + '' + config.subdominio + '' + dominioBase;

    return (
        <div className="fade-in">
            {/* INFORMACIÓN EMPRESA */}
            <Card
                title={<span style={{ fontSize: isMobile ? "15px" : "17px" }}><ShopOutlined /> Información de la Empresa</span>}
                extra={<Button type="link" icon={<EditOutlined />} onClick={() => setIsModalEmpresaOpen(true)} title='Editar información'>Editar</Button>}
                style={{ borderRadius: 12 }}
            >
                {!isMobile ? (
                    <Row gutter={[32, 32]}>
                        <Col span={4}>
                            <Avatar
                                size={100}
                                shape="square"
                                icon={!config.logo_url ? <ShopOutlined /> : null}
                                src={config.logo_url}
                                style={{ backgroundColor: '#f0f2f5', color: '#22BABB', border: '1px solid #e2e8f0' }}
                            />
                        </Col>
                        <Col span={20}>
                            <Title level={3} style={{ margin: 0 }}>
                                {config.nombre || "Nombre no definido"}
                                {config.identificacion_fiscal && <Tag color="blue" style={{ marginLeft: 10, verticalAlign: 'middle' }}>NIT: {config.identificacion_fiscal}</Tag>}
                            </Title>
                            <Text type="secondary" style={{ display: 'block', marginBottom: '15px' }}>
                                Razón Social: {config.nombre_legal || "No definida"}
                            </Text>

                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Space><MailOutlined /> <Text>{config.email || "Sin correo registrado"}</Text></Space>
                                </Col>
                                <Col span={12}>
                                    <Space><PhoneOutlined /> <Text>{config.telefono || "Sin teléfono"}</Text></Space>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <Row align="middle" wrap={false} style={{ width: '100%' }}>
                            <Col flex="auto">
                                <Row style={{ width: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                                    <Avatar
                                        size={{ xs: 100, sm: 100, md: 100, lg: 100, xl: 100, xxl: 100 }}
                                        shape="square"
                                        icon={!config.logo_url ? <ShopOutlined /> : null}
                                        src={config.logo_url}
                                        style={{ marginBottom: '12px', backgroundColor: '#f0f2f5', color: '#22BABB', border: '1px solid #e2e8f0' }}
                                    />
                                </Row>
                                <Row>
                                    <Title style={{ marginBottom: '12px', fontSize: "17px" }}>{config.nombre || "Nombre no definido"}</Title>
                                </Row>

                                <Row style={{ marginBottom: '12px' }}>
                                    <Text>{config.identificacion_fiscal && <Tag color="blue" style={{ verticalAlign: 'middle' }}>NIT: {config.identificacion_fiscal}</Tag>}</Text>
                                </Row>

                                <Row style={{ marginBottom: '12px' }}>
                                    <Text type="secondary">Razón Social: {config.nombre_legal || "No definida"}</Text>
                                </Row>

                                <Row style={{ marginBottom: '12px' }}>
                                    <Text><MailOutlined /><Text> {config.email || "Sin correo registrado"}</Text></Text>
                                </Row>

                                <Row>
                                    <Text><PhoneOutlined /><Text> {config.telefono || "Sin teléfono"}</Text></Text>
                                </Row>
                            </Col>
                        </Row>
                    </>
                )}
            </Card>

            {/* INFORMACIÓN DE LA DIRECCIÓN */}
            <Card
                title={<span style={{ fontSize: isMobile ? "15px" : "17px" }}><EnvironmentOutlined /> Información de la Dirección</span>}
                extra={<Button type="link" icon={<EditOutlined />} onClick={() => setIsModalDirEmpresaOpen(true)} title='Editar información'>Editar</Button>}
                style={{ borderRadius: 12, marginTop: 24 }}
            >
                {!isMobile ? (
                    <Row gutter={[16, 24]}>
                        <Col span={12}><Text type="secondary">País:</Text> <Text strong style={{ marginLeft: 8 }}>{config.pais || "No registrado"}</Text></Col>
                        <Col span={12}><Text type="secondary">Departamento:</Text> <Text strong style={{ marginLeft: 8 }}>{config.provincia || "No registrado"}</Text></Col>
                        <Col span={12}><Text type="secondary">Ciudad:</Text> <Text strong style={{ marginLeft: 8 }}>{config.ciudad || "No registrada"}</Text></Col>

                        <Col span={12}><Text type="secondary">Dirección Física:</Text> <Text strong style={{ marginLeft: 8 }}>{config.direccion || "No registrada"}</Text></Col>

                        <Col span={12}><Text type="secondary">Código postal:</Text> <Text strong style={{ marginLeft: 8 }}>{config.code_postal || "-------"}</Text></Col>
                    </Row>
                ) : (
                    <>
                        <Row style={{ marginBottom: '12px' }}>
                            <Text type="secondary">País:</Text> <Text strong style={{ marginLeft: 8 }}>{config.pais || "No registrado"}</Text>
                        </Row>
                        <Row style={{ marginBottom: '12px' }}>
                            <Text type="secondary">Departamento:</Text> <Text strong style={{ marginLeft: 8 }}>{config.provincia || "No registrado"}</Text>
                        </Row>
                        <Row style={{ marginBottom: '12px' }}>
                            <Text type="secondary">Ciudad:</Text> <Text strong style={{ marginLeft: 8 }}>{config.ciudad || "No registrada"}</Text>
                        </Row>
                        <Row style={{ marginBottom: '12px' }}>
                            <Text type="secondary">Dirección Física:</Text> <Text strong style={{ marginLeft: 8 }}>{config.direccion || "No registrada"}</Text>
                        </Row>
                        <Row>
                            <Text type="secondary">Código postal:</Text> <Text strong style={{ marginLeft: 8 }}>{config.code_postal || "-------"}</Text>
                        </Row>
                    </>
                )}
            </Card>

            <Card
                title={<span style={{ fontSize: isMobile ? "15px" : "17px" }}><GlobalOutlined /> Configuración Regional</span>}
                extra={<Button type="link" icon={<EditOutlined />} onClick={() => setIsModalRegionOpen(true)} title='Editar información'>Editar</Button>}
                style={{ borderRadius: 12, marginTop: 24 }}
            >
                {!isMobile ? (
                    <Row gutter={[16, 24]}>
                        <Col span={12}>
                            <Text type="secondary">Zona horaria:</Text>
                            <Text strong style={{ marginLeft: 8 }}>
                                {config.settings?.zona_horaria === 'colombia' ? '(GMT -5:0) Hora de Colombia' : '(GMT -5:0) Hora de Colombia'}
                            </Text>
                        </Col>
                        <Col span={12}>
                            <Text type="secondary">Idioma y Moneda:</Text>
                            <Text strong style={{ marginLeft: 8 }}>
                                {config.settings?.idioma_moneda === 'es_cop' ? 'Español - COP ($)' : 'Español - COP ($)'}
                            </Text>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <Row style={{ marginBottom: '12px' }}>
                            <Text type="secondary">Zona horaria:</Text>
                            <Text strong style={{ marginLeft: 8 }}>
                                {config.settings?.zona_horaria === 'colombia' ? '(GMT -5:0) Hora de Colombia' : '(GMT -5:0) Hora de Colombia'}
                            </Text>
                        </Row>
                        <Row>
                            <Text type="secondary">Idioma y Moneda:</Text>
                            <Text strong style={{ marginLeft: 8 }}>
                                {config.settings?.idioma_moneda === 'es_cop' ? 'Español - COP ($)' : 'Español - COP ($)'}
                            </Text>
                        </Row>
                    </>
                )}
            </Card>

            <Card
                title={<span style={{ fontSize: isMobile ? "15px" : "17px" }}><TagsOutlined /> Gestión de Tarifas (Listas de Precios)</span>}
                extra={<Button type="link" icon={<EditOutlined />} onClick={() => {
                    formTarifas.setFieldsValue({
                        lista_1_activa: defaultTarifas.lista_1.activa, lista_1_nombre: defaultTarifas.lista_1.nombre,
                        lista_2_activa: defaultTarifas.lista_2.activa, lista_2_nombre: defaultTarifas.lista_2.nombre,
                        lista_3_activa: defaultTarifas.lista_3.activa, lista_3_nombre: defaultTarifas.lista_3.nombre,
                    });
                    setIsModalTarifasOpen(true);
                }} title='Editar información'>Editar</Button>}
                style={{ borderRadius: 12, marginTop: 24 }}
            >
                <Text type="secondary" style={{ display: 'block', marginBottom: '15px' }}>
                    Activa y personaliza el nombre de las listas de precios exclusivas (Ej: Mayorista, VIP) para asignarlas a tus clientes.
                </Text>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {defaultTarifas.lista_1.activa && <Tag color="blue" style={{ fontSize: '14px', padding: '4px 10px' }}>{defaultTarifas.lista_1.nombre}</Tag>}
                    {defaultTarifas.lista_2.activa && <Tag color="cyan" style={{ fontSize: '14px', padding: '4px 10px' }}>{defaultTarifas.lista_2.nombre}</Tag>}
                    {defaultTarifas.lista_3.activa && <Tag color="geekblue" style={{ fontSize: '14px', padding: '4px 10px' }}>{defaultTarifas.lista_3.nombre}</Tag>}

                    {!defaultTarifas.lista_1.activa && !defaultTarifas.lista_2.activa && !defaultTarifas.lista_3.activa && (
                        <Text type="secondary" italic>No tienes listas de precios adicionales activas.</Text>
                    )}
                </div>
            </Card>

            {/* CATÁLOGO DIGITAL */}
            <Card
                title={<span style={{ fontSize: isMobile ? "15px" : "17px" }}><LinkOutlined /> Mi Catálogo Digital</span>}
                extra={<Button type="link" icon={<EditOutlined />} onClick={() => {
                    formCatalogo.setFieldsValue({ subdominio: config.subdominio });
                    setIsModalCatalogoOpen(true);
                }} title='Editar información'>Editar</Button>}
                style={{ borderRadius: 12, marginTop: 24 }}
            >
                <Row gutter={[16, 24]} align="middle">
                    <Col span={24}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: '10px' }}>
                            Comparte este enlace con tus clientes para que vean todo el catálogo de tus productos y realicen pedidos por WhatsApp.
                        </Text>

                        <Text type="secondary" style={{ display: 'block', marginBottom: '10px' }}>
                            Para elegir que productos quieres mostrar en el catálogo virtual, lo debes hacer desde el módulo de productos editando el producto.
                        </Text>

                        {config.subdominio ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <div style={{
                                    width: isMobile ? '100%' : '400px',
                                    height: '44px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0px 14px',
                                    background: '#fff',
                                    border: '1px solid #7dd3fc',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    color: '#0369a1',
                                    fontSize: isMobile ? '14px' : '16px',
                                    wordBreak: 'break-all'
                                }}>
                                    <a
                                        href={urlEcomerce}
                                        target='_blank'
                                        title='Abrir catalogo digital'
                                    >
                                        {urlEcomerce}
                                    </a>
                                </div>
                                <Button
                                    type="primary"
                                    icon={<CopyOutlined />}
                                    onClick={() => copiarAlPortapapeles(`${protocolo}${config.subdominio}${dominioBase}`)}
                                    style={{ backgroundColor: '#0284c7', height: '43px', color: '#fff', fontWeight: '500' }}
                                    title='Copiar enlace'
                                >
                                    Copiar Enlace
                                </Button>
                            </div>
                        ) : (
                            <Button type="dashed" danger onClick={() => setIsModalCatalogoOpen(true)}>
                                Aún no has creado tu enlace. ¡Créalo ahora!
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card>

            <Card
                title={<span style={{ fontSize: isMobile ? "15px" : "17px" }}><CreditCardOutlined /> Configuración de Cartera</span>}
                extra={<Button type="link" icon={<EditOutlined />} onClick={handleOpenModalCartera} disabled={configLoading} title='Editar información'>Editar</Button>}
                style={{ borderRadius: 12, marginTop: 24 }}
            >
                <Row gutter={[16, 24]}>
                    <Col span={24}>
                        <Space size={6} style={{ cursor: 'help' }}>
                            <Text type="secondary">Días para considerar cartera vencida:</Text>
                            <Tooltip title="Facturas o créditos con más de este número de días se suman en 'Cartera vencida' (vista Clientes).">
                                <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                            </Tooltip>
                        </Space>
                        <Text strong style={{ marginLeft: 8, color: '#22BABB', fontSize: '15px' }}>
                            {config.settings?.dias_vencimiento_cartera === 0 ? 'Toda la cartera pendiente' : `${config.settings?.dias_vencimiento_cartera} días`}
                        </Text>
                    </Col>
                </Row>
            </Card>

            {/* NUEVA TARJETA: CONFIGURACIÓN POS */}
            <Card
                title={<span style={{ fontSize: isMobile ? "15px" : "17px" }}><ShopOutlined /> Configuración del Punto de Venta</span>}
                extra={<Button type="link" icon={<EditOutlined />} onClick={() => {
                    formPos.setFieldsValue({
                        permitir_edicion_precios: config.settings?.permitir_edicion_precios === 'true' || config.settings?.permitir_edicion_precios === true
                    });
                    setIsModalPosOpen(true);
                }}>Editar</Button>}
                style={{ borderRadius: 12, marginTop: 24 }}
            >
                <Row gutter={[16, 24]}>
                    <Col span={24}>
                        <Text type="secondary">Permitir edición manual de precios en el carrito:</Text>
                        <Text strong style={{ marginLeft: 8, color: (config.settings?.permitir_edicion_precios === 'true' || config.settings?.permitir_edicion_precios === true) ? '#16a34a' : '#ef4444' }}>
                            {(config.settings?.permitir_edicion_precios === 'true' || config.settings?.permitir_edicion_precios === true) ? 'SÍ (Activado)' : 'NO (Desactivado)'}
                        </Text>
                    </Col>
                </Row>
            </Card>

            {/* CONFIGURACIÓN DE TOMA DE PEDIDOS */}
            <Card
                title={<span style={{ fontSize: isMobile ? "15px" : "17px" }}><ShopOutlined /> Módulo de Toma de Pedidos (Rutas / Calle)</span>}
                extra={<Button type="link" icon={<EditOutlined />} onClick={() => {
                    formPedidos.setFieldsValue({
                        modulo_pedidos_activo: config.modulo_pedidos_activo === true || config.modulo_pedidos_activo === 'true'
                    });
                    setIsModalPedidosOpen(true);
                }}>Editar</Button>}
                style={{ borderRadius: 12, marginTop: 24 }}
            >
                <Row gutter={[16, 24]}>
                    <Col span={24}>
                        <Text type="secondary">Toma de pedidos en la terminal de ventas (Modo Ruta/Calle):</Text>
                        <Text strong style={{ marginLeft: 8, color: (config.modulo_pedidos_activo === true || config.modulo_pedidos_activo === 'true') ? '#16a34a' : '#ef4444' }}>
                            {(config.modulo_pedidos_activo === true || config.modulo_pedidos_activo === 'true') ? 'ACTIVADO' : 'DESACTIVADO'}
                        </Text>
                    </Col>
                </Row>
            </Card>

            {/* NUEVA TARJETA: CONFIGURACIÓN DE INVENTARIO */}
            <Card
                title={<span style={{ fontSize: isMobile ? "15px" : "17px" }}><DatabaseOutlined /> Configuración de Inventario</span>}
                extra={<Button type="link" icon={<EditOutlined />} onClick={() => {
                    formInventario.setFieldsValue({
                        controlar_stock: config.settings?.controlar_stock === 'true' || config.settings?.controlar_stock === true ? 'true' : 'false'
                    });
                    setIsModalInventarioOpen(true);
                }}>Editar</Button>}
                style={{ borderRadius: 12, marginTop: 24 }}
            >
                <Row gutter={[16, 24]}>
                    <Col span={24}>
                        <Text type="secondary">Bloquear ventas al agotar stock:</Text>
                        <Text strong style={{ marginLeft: 8, color: (config.settings?.controlar_stock === 'true' || config.settings?.controlar_stock === true) ? '#16a34a' : '#ef4444' }}>
                            {(config.settings?.controlar_stock === 'true' || config.settings?.controlar_stock === true) ? 'SÍ (Bloqueado)' : 'NO (Permitir vender sin stock)'}
                        </Text>
                    </Col>
                </Row>
            </Card>

            {/* MODAL CONFIGURACIÓN TOMA DE PEDIDOS */}
            <Modal
                title={null}
                open={isModalPedidosOpen}
                onCancel={() => setIsModalPedidosOpen(false)}
                footer={null}
                centered
                width={480}
                zIndex={1050}
                styles={{ mask: { background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(5px)' } }}
            >
                <div style={{ textAlign: 'center', padding: '15px 0 20px' }}>
                    <div style={{ background: '#fef3c7', width: '55px', height: '55px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                        <ShopOutlined style={{ fontSize: '26px', color: '#d97706' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, fontSize: '20px' }}>Módulo Toma de Pedidos</Title>
                    <Text type="secondary" style={{ marginTop: '8px', display: 'block' }}>
                        Permite a los vendedores alternar entre Venta Directa y Toma de Pedido en la terminal POS.
                    </Text>
                </div>

                <Form
                    form={formPedidos}
                    layout="vertical"
                    onFinish={async (values) => {
                        try {
                            await updateEmpresa({ modulo_pedidos_activo: values.modulo_pedidos_activo });
                            message.success('Configuración de pedidos actualizada exitosamente.');
                            setIsModalPedidosOpen(false);
                        } catch (err) {
                            message.error('Error al guardar la configuración de pedidos.');
                        }
                    }}
                >
                    <Form.Item
                        name="modulo_pedidos_activo"
                        label={<Text strong style={{ fontSize: '13px', color: '#334155' }}>Estado del Módulo</Text>}
                        valuePropName="checked"
                    >
                        <Select
                            size="large"
                            style={{ height: '50px' }}
                            options={[
                                { value: true, label: 'Habilitado (Mostrar switcher Venta / Pedido)' },
                                { value: false, label: 'Deshabilitado (Ocultar opción de pedidos)' }
                            ]}
                        />
                    </Form.Item>

                    <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                        <Text type="secondary" style={{ fontSize: '12.5px', lineHeight: '1.5', display: 'block' }}>
                            <strong style={{ color: '#0f172a' }}>Nota:</strong> Al activar esta opción, los usuarios autorizados mediante el permiso <Tag color="orange">pedidos:crear</Tag> podrán tomar pedidos desde dispositivos móviles sin afectar la caja ni facturar inventario de inmediato.
                        </Text>
                    </div>

                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        size="large"
                        loading={configLoading}
                        disabled={configLoading}
                        style={{ background: '#22BABB', height: '55px', borderRadius: '12px', fontWeight: '800', fontSize: '15px', border: 'none', letterSpacing: '0.5px' }}
                    >
                        GUARDAR CONFIGURACIÓN
                    </Button>
                </Form>
            </Modal>

            {/* MODAL CONFIGURACIÓN INVENTARIO */}
            <Modal
                title={null}
                open={isModalInventarioOpen}
                onCancel={() => setIsModalInventarioOpen(false)}
                footer={null}
                centered
                width={480}
                zIndex={1050}
                styles={{ mask: { background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(5px)' } }}
            >
                <div style={{ textAlign: 'center', padding: '15px 0 20px' }}>
                    <div style={{ background: '#f0f9ff', width: '55px', height: '55px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                        <DatabaseOutlined style={{ fontSize: '26px', color: '#0284c7' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, fontSize: '20px' }}>Gestión de Inventario</Title>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text type="secondary" style={{ marginTop: '8px' }}>
                            Define las políticas de control de existencias para tu punto de venta.
                        </Text>
                    </div>
                </div>

                <Form
                    form={formInventario}
                    layout="vertical"
                    onFinish={async (values) => {
                        try {
                            await updateConfig({ controlar_stock: values.controlar_stock });
                            message.success('Política de inventario actualizada exitosamente.');
                            setIsModalInventarioOpen(false);
                        } catch (err) {
                            message.error('Error al guardar la configuración de inventario.');
                        }
                    }}
                >
                    <Form.Item
                        name="controlar_stock"
                        label={<Text strong style={{ fontSize: '13px', color: '#334155' }}>Política de Ventas sin Stock</Text>}
                    >
                        <Select
                            size="large"
                            style={{ height: '50px' }} // Altura ideal para pantallas táctiles
                            options={[
                                { value: 'false', label: 'Permitir ventas en negativo (Sin restricción)' },
                                { value: 'true', label: 'Bloquear ventas al agotar existencias (Estricto)' }
                            ]}
                        />
                    </Form.Item>

                    <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                        <Text type="secondary" style={{ fontSize: '12.5px', lineHeight: '1.5', display: 'block' }}>
                            <strong style={{ color: '#0f172a' }}>Nota técnica:</strong> Al habilitar el control estricto, el sistema impedirá añadir al carrito cualquier producto cuyo inventario sea igual o inferior a cero, garantizando así la exactitud de tu inventario físico.
                        </Text>
                    </div>

                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        size="large"
                        loading={configLoading}
                        disabled={configLoading}
                        style={{ background: '#22BABB', height: '55px', borderRadius: '12px', fontWeight: '800', fontSize: '15px', border: 'none', letterSpacing: '0.5px' }}
                    >
                        GUARDAR POLÍTICA
                    </Button>
                </Form>
            </Modal>

            {/* MODAL CONFIGURACIÓN POS */}
            <ModalPuntoDeVentaPos
                isModalPosOpen={isModalPosOpen}
                setIsModalPosOpen={setIsModalPosOpen}
                formPos={formPos}
                configLoading={configLoading}
                updateConfig={updateConfig}
            />

            <ModalConfiguracionCartera
                isModalTarifasOpen={isModalTarifasOpen}
                setIsModalTarifasOpen={setIsModalTarifasOpen}
                formTarifas={formTarifas}
                configLoading={configLoading}
                handleGuardarTarifas={handleGuardarTarifas}
            />

            <Modal
                title="Configuración de Cartera"
                open={isModalCarteraOpen}
                onCancel={() => setIsModalCarteraOpen(false)}
                onOk={() => formCartera.submit()}
                okText="Guardar"
                confirmLoading={guardandoCartera}
                okButtonProps={{ style: { backgroundColor: '#22BABB', borderColor: '#22BABB' } }}
            >
                <Form
                    form={formCartera}
                    layout="vertical"
                    onFinish={handleSubmitCartera}
                    style={{ marginTop: 20 }}
                >
                    <Form.Item
                        name="dias_vencimiento_cartera"
                        label="Días para considerar cartera vencida"
                        rules={[{ required: true, message: 'Indica el número de días' }]}
                        initialValue={config.settings?.dias_vencimiento_cartera}
                    >
                        <InputNumber min={0} max={365} style={{ width: '100%' }} placeholder="Ej: 30" />
                    </Form.Item>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Con 0 se considera toda la cartera pendiente. Facturas o créditos con más de este número de días se suman en la tarjeta &quot;Cartera vencida&quot; en la vista Clientes.
                    </Text>
                </Form>
            </Modal>

            <Modal
                title={<Space><EditOutlined /> Editar Información Legal</Space>}
                open={isModalEmpresaOpen}
                onCancel={() => {
                    setIsModalEmpresaOpen(false);
                    formEmpresa.resetFields();
                    setLogoFileBase64(null);
                }}
                onOk={() => formEmpresa.submit()}
                confirmLoading={configLoading}
                width={650}
                okText="Guardar Cambios"
                okButtonProps={{ style: { backgroundColor: '#22BABB' } }}
            >
                <Form
                    form={formEmpresa}
                    layout="vertical"
                    onFinish={handleGuardarEmpresa}
                    style={{ marginTop: 10 }}
                    initialValues={{
                        nombre: config.nombre,
                        nombre_legal: config.nombre_legal,
                        identificacion_fiscal: config.identificacion_fiscal,
                        email: config.email,
                        telefono: config.telefono
                    }}
                >
                    {!isMobile ? (
                        <Row gutter={16}>
                            <Col span={24} style={{ textAlign: 'center', marginBottom: 20 }}>
                                <Upload
                                    showUploadList={false}
                                    beforeUpload={(file) => {
                                        const reader = new FileReader();
                                        reader.onload = (e) => setLogoFileBase64(e.target.result);
                                        reader.readAsDataURL(file);
                                        return false;
                                    }}
                                >
                                    <Badge count={<Button shape="circle" icon={<EditOutlined />} size="small" />} offset={[-10, 80]}>
                                        <Avatar
                                            size={100}
                                            shape="square"
                                            icon={!logoFileBase64 && !config.logo_url ? <ShopOutlined /> : null}
                                            src={logoFileBase64 || config.logo_url}
                                            style={{ backgroundColor: '#f0f2f5', color: '#22BABB', cursor: 'pointer', border: '1px solid #d9d9d9' }}
                                        />
                                    </Badge>
                                </Upload>
                                <Text type="secondary" style={{ display: 'block', marginTop: '10px', fontSize: '12px' }}>
                                    Haz clic en la imagen para cambiar el logo (Max 2MB)
                                </Text>
                            </Col>

                            <Col span={12}><Form.Item name="nombre" label="Nombre Comercial" rules={[{ required: true }]}><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item name="nombre_legal" label="Razón Social (Para facturación)"><Input /></Form.Item></Col>

                            <Col span={12}><Form.Item name="identificacion_fiscal" label="NIT / Cédula"><Input /></Form.Item></Col>
                            <Col span={12}><Form.Item name="email" label="Correo Electrónico"><Input prefix={<MailOutlined />} /></Form.Item></Col>

                            <Col span={24}><Form.Item name="telefono" label="Teléfono o WhatsApp"><Input prefix={<PhoneOutlined />} /></Form.Item></Col>
                        </Row>
                    ) : (
                        <>
                            <Row style={{ width: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center', marginBottom: '12px' }}>
                                <Upload
                                    showUploadList={false}
                                    beforeUpload={(file) => {
                                        const reader = new FileReader();
                                        reader.onload = (e) => setLogoFileBase64(e.target.result);
                                        reader.readAsDataURL(file);
                                        return false;
                                    }}
                                >
                                    <Badge count={<Button shape="circle" icon={<EditOutlined />} size="small" />} offset={[-10, 80]}>
                                        <Avatar
                                            size={100}
                                            shape="square"
                                            icon={!logoFileBase64 && !config.logo_url ? <ShopOutlined /> : null}
                                            src={logoFileBase64 || config.logo_url}
                                            style={{ backgroundColor: '#f0f2f5', color: '#22BABB', cursor: 'pointer', border: '1px solid #d9d9d9' }}
                                        />
                                    </Badge>
                                </Upload>
                                <Text type="secondary" style={{ display: 'block', marginTop: '10px', fontSize: '12px' }}>
                                    Haz clic en la imagen para cambiar el logo (Max 2MB)
                                </Text>
                            </Row>

                            <Row>
                                <Col span={24}>
                                    <Form.Item name="nombre" label="Nombre Comercial" rules={[{ required: true }]}><Input style={{ width: '100%' }} /></Form.Item>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={24}>
                                    <Form.Item name="nombre_legal" label="Razón Social (Para facturación)"><Input /></Form.Item>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={24}>
                                    <Form.Item name="identificacion_fiscal" label="NIT / Cédula"><Input /></Form.Item>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={24}>
                                    <Form.Item name="email" label="Correo Electrónico"><Input prefix={<MailOutlined />} /></Form.Item>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={24}>
                                    <Form.Item name="telefono" label="Teléfono o WhatsApp"><Input prefix={<PhoneOutlined />} /></Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                </Form>
            </Modal>

            {/* CONFIGURAR CATÁLOGO */}
            <Modal
                title={<Space><LinkOutlined style={{ color: '#0369a1' }} /> Crear Enlace de Catálogo</Space>}
                open={isModalCatalogoOpen}
                onCancel={() => {
                    setIsModalCatalogoOpen(false);
                    formCatalogo.resetFields();
                }}
                onOk={() => formCatalogo.submit()}
                confirmLoading={configLoading}
                okText="Guardar Enlace"
                okButtonProps={{ style: { backgroundColor: '#0284c7' } }}
            >
                <div style={{ marginBottom: '20px' }}>
                    <Text type="secondary">
                        Elige un nombre corto y fácil de recordar para tu tienda.
                        <strong> Sólo usa letras minúsculas, números o guiones (-). Sin espacios.</strong>
                    </Text>
                </div>

                <Form
                    form={formCatalogo}
                    layout="vertical"
                    onFinish={handleGuardarCatalogo}
                    initialValues={{ subdominio: config.subdominio }}
                >
                    <Form.Item
                        name="subdominio"
                        label="Nombre de tu tienda (Subdominio)"
                        rules={[
                            { required: true, message: 'El enlace es requerido' },
                            {
                                pattern: /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
                                message: 'solo minúsculas, números y guiones intermedios. sin espacios y se recomienda un subdominio sin guiones.'
                            },
                            { min: 2, message: 'Mínimo 2 caracteres' },
                            { max: 25, message: 'Máximo 25 caracteres' }
                        ]}
                    >
                        <Input
                            addonAfter=".tienda.yugonub.com"
                            placeholder="ejemplo-tienda"
                            size="large"
                            onChange={(e) => {
                                formCatalogo.setFieldsValue({ subdominio: e.target.value.toLowerCase().replace(/\s+/g, '-') });
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={<Space><EnvironmentOutlined /> Editar Dirección de la Empresa</Space>}
                open={isModalDirEmpresaOpen}
                onCancel={() => {
                    setIsModalDirEmpresaOpen(false);
                    formDirEmpresa.resetFields();
                    setDepartamentoActivo(null);
                }}
                onOk={() => formDirEmpresa.submit()}
                confirmLoading={configLoading}
                width={600}
                okText="Guardar Dirección"
                okButtonProps={{ style: { backgroundColor: '#22BABB' } }}
            >
                <Form
                    form={formDirEmpresa}
                    layout="vertical"
                    onFinish={handleGuardarDirEmpresa}
                    style={{ marginTop: 20 }}
                    initialValues={{
                        direccion: config.direccion,
                        provincia: config.provincia,
                        ciudad: config.ciudad,
                        code_postal: config.code_postal,
                        pais: config.pais || 'Colombia'
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="pais" label="País">
                                <Select>
                                    <Select.Option value="Colombia">Colombia</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="provincia" label="Departamento">
                                <Select
                                    showSearch
                                    placeholder="Selecciona el departamento"
                                    options={Object.keys(COLOMBIA_DATA).map(dpto => ({ label: dpto, value: dpto }))}
                                    onChange={(valor) => {
                                        setDepartamentoActivo(valor);
                                        formDirEmpresa.setFieldsValue({ ciudad: undefined });
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="ciudad" label="Ciudad">
                                <Select
                                    showSearch
                                    placeholder="Selecciona la ciudad"
                                    disabled={!departamentoActivo && !config.provincia}
                                    options={
                                        (COLOMBIA_DATA[departamentoActivo || config.provincia] || []).map(ciudad => ({
                                            label: ciudad,
                                            value: ciudad
                                        }))
                                    }
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="code_postal" label="Código Postal">
                                <Input placeholder="Ej: 050036" />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item name="direccion" label="Dirección de la Empresa (útil para las facturas)">
                                <Input prefix={<EnvironmentOutlined style={{ color: '#bfbfbf' }} />} placeholder="Ej: Cra 87 A # 68 B 78" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title={<Space><GlobalOutlined /> Editar Configuración Regional</Space>}
                open={isModalRegionOpen}
                onCancel={() => {
                    setIsModalRegionOpen(false);
                    formRegion.resetFields();
                }}
                onOk={() => formRegion.submit()}
                confirmLoading={configLoading}
                width={500}
                okText="Guardar Cambios"
                okButtonProps={{ style: { backgroundColor: '#22BABB' } }}
            >
                <Form
                    form={formRegion}
                    layout="vertical"
                    onFinish={handleGuardarRegion}
                    style={{ marginTop: 20 }}
                    initialValues={{
                        zona_horaria: config.settings?.zona_horaria || 'colombia',
                        idioma_moneda: config.settings?.idioma_moneda || 'es_cop'
                    }}
                >
                    <Form.Item name="zona_horaria" label="Zona Horaria">
                        <Select>
                            <Select.Option value="colombia">(GMT -5:0) Hora de Colombia (America/Bogota)</Select.Option>
                            <Select.Option value="mexico">(GMT -6:0) Hora de México (America/Mexico_City)</Select.Option>
                            <Select.Option value="argentina">(GMT -3:0) Hora de Argentina (America/Argentina/Buenos_Aires)</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="idioma_moneda" label="Idioma y Moneda por Defecto">
                        <Select>
                            <Select.Option value="es_cop">Español - Peso Colombiano (COP $)</Select.Option>
                            <Select.Option value="es_mxn">Español - Peso Mexicano (MXN $)</Select.Option>
                            <Select.Option value="en_usd">Inglés - Dólar Estadounidense (USD $)</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SeccionEmpresa;